import React, { Component, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import {
  SR_API_ACTION_PARTNER_SIGNED_REFERRAL_AGREEMENT_2023,
} from 'api/actionTypes';

import {
  PARTNER_TYPE_AGENT,
} from 'common/partnerConstants';

import {
  tenantScreeningEnabled,
  sunRiseEnabled,
} from 'common/partnerHelpers';

import {
  formatPercentage,
} from 'common/formatters';

import SrPartnerMarketListingModal, {
  FORM_TYPE_MARKET_LISTING,
} from 'client/components/SrPartnerMarketListingModal';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE, SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrPartnerPortalCard from 'client/components/SrPartnerPortalCard/SrPartnerPortalCard';
import SrPartnerReferralAgreement2023Modal from 'client/components/SrPartnerReferralAgreement2023Modal';

import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import PartnerActions from 'client/actions/Partner';

const SrPartnerTenantScreeningCTAs = ({ onFullServiceClick, onTenantScreeningClick, partner }) => {
  return (
    <div className='SrPartnerTenantScreeningCriteriaCTAs'>
      <SrPartnerPortalCard
        icon='/images/partnerPortal/full-service-icon.svg'
        title='Full-Service Leasing'
        firstDetail='Get a lease signed 26% faster than the market'
        secondDetail='Tours, App Processing, Lease Drafting & more'
        thirdDetail={`Sunroom bills you ${formatPercentage(partner.commissionPercentage)} of 1st month's rent`} // eslint-disable-line max-len
        buttonText='Market Property'
        onButtonClick={onFullServiceClick}
        buttonTheme={SR_BUTTON_THEME_ADOBE_GREEN}
        footerHighlightedText='Lease 26% '
        footerNormalText='faster than the market'
      />
      <SrPartnerPortalCard
        icon='/images/partnerPortal/tenant-screening-icon.svg'
        title='Screen Tenants for Free'
        firstDetail='Credit, Background, & Rental History'
        secondDetail='Fraud Prevention & Faster Apps'
        thirdDetail='Free for you. App fee paid by tenants'
        buttonText='Start Screening'
        onButtonClick={onTenantScreeningClick}
        buttonTheme={SR_BUTTON_THEME_ADOBE_BLUE}
        footerHighlightedText='30 seconds '
        footerNormalText='to setup'
      />
    </div>
  );
};

@withRouter
@branch()
class SrPartnerMarketListing extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired,
    propertyAttributes: PropTypes.array,
    history: PropTypes.object.isRequired,
    reloadDataTableMarketingPartnerListing: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      isModalOpen: false,
      isReferralAgreementModalOpen: false,
    };
  }

  _toggleModal = () => {
    this.setState({
      isModalOpen: !this.state.isModalOpen,
    });
  }

  _onClickMarketProperty = () => {
    const {
      partner,
      history,
    } = this.props;
    const isAgent = partner.type === PARTNER_TYPE_AGENT;
    if (isAgent) {
      history.push(`/partner/${partner.slug}/market-property`);
    }
    else {
      this._toggleModal();
    }
  }

  _onClickScreenTenants = () => {
    const { history, partner } = this.props;
    return history.push(`/partner/${partner.slug}/market-screening-property`);
  }

  _closeReferralAgreementModal = () => {
    this.setState({
      isReferralAgreementModalOpen: false,
    });
  }

  _onAcceptReferralAgreement2023 = () => {
    const { dispatch, partner } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_SIGNED_REFERRAL_AGREEMENT_2023,
      payload: {
        partnerId: partner.id,
      },
    }).then(() => {
      this._onClickMarketProperty();

      dispatch(PartnerActions.loadPartnerBySlug, {
        slug: partner.slug,
        forceRefresh: true,
      });
    });

  }

  render() {
    const {
      isModalOpen,
      isReferralAgreementModalOpen,
    } = this.state;
    const {
      history,
      partner,
      propertyAttributes,
      reloadDataTableMarketingPartnerListing,
      currentUser,
    } = this.props;

    const isTenantScreeningEnabled = tenantScreeningEnabled(partner);
    const isSunriseEnabled = sunRiseEnabled(partner);
    const marketPropertyCardTitle = !isSunriseEnabled
      ? 'Market your property now!'
      : 'Order a listing setup';
    const marketPropertyButtonLabel = !isSunriseEnabled
      ? 'Market Property'
      : 'Order Listing Setup';
    const onClickMarketProperty = !isSunriseEnabled
      ? this._onClickMarketProperty
      : () => history.push(`/partner/${partner.slug}/market-sunrise-property`);

    return (
      <>
        {isTenantScreeningEnabled ?
          (
            <>
              <SrText
                size='40'
                weight='800'
                display='block'
                align='center'
                font='greycliff'
                padding='28px'
              >
                Leasing Portal
              </SrText>
              <SrPartnerTenantScreeningCTAs
                onFullServiceClick={() => {
                  if (!partner.referralAgreement2023SignedDate) {
                    this.setState({
                      isReferralAgreementModalOpen: true,
                    });
                  }
                  else {
                    this._onClickMarketProperty();
                  }
                }}
                onTenantScreeningClick={this._onClickScreenTenants}
                partner={partner}
              />
            </>
          )
          : (
            <MarketPropertyCardButton
              cardTitle={marketPropertyCardTitle}
              buttonLabel={marketPropertyButtonLabel}
              partner={partner}
              onClickMarketProperty={onClickMarketProperty}
              onAcceptReferralAgreement2023={this._onAcceptReferralAgreement2023}
            />
          )
        }
        {isModalOpen && (
          <SrPartnerMarketListingModal
            isNew
            partner={partner}
            propertyAttributes={propertyAttributes}
            reloadDataTableMarketingPartnerListing={reloadDataTableMarketingPartnerListing}
            onClose={this._toggleModal}
            currentUser={currentUser}
            formType={FORM_TYPE_MARKET_LISTING}
          />
        )}
        {isReferralAgreementModalOpen && (
          <SrPartnerReferralAgreement2023Modal
            partner={partner}
            onClose={this._closeReferralAgreementModal}
            onSubmit={() => {
              this._onAcceptReferralAgreement2023();
              this._closeReferralAgreementModal();
            }}
          />
        )}
      </>
    );
  }
}

const MarketPropertyCardButton = ({
  cardTitle,
  buttonLabel,
  partner,
  onClickMarketProperty,
  onAcceptReferralAgreement2023,
}) => {
  const [isReferralAgreementModalOpen, setIsReferralAgreementModalOpen] = useState(false);

  const marginTop = partner.type === PARTNER_TYPE_AGENT || sunRiseEnabled(partner)
    ? '0px'
    : '42px';

  const _onClickMarketProperty = () => {
    if (!partner.referralAgreement2023SignedDate) {
      setIsReferralAgreementModalOpen(true);
    }
    else {
      onClickMarketProperty();
    }
  };

  return (
    <SrBox
      className='SrPartnerMarketListing'
      marginTop={marginTop}
    >
      <SrText
        color='blueGreycliff'
        font='greycliff'
        size='24'
        lineHeight='36px'
        weight='800'
        align='center'
      >
        {cardTitle}
      </SrText>
      <SrBox maxWidth='311px' width='calc(100% - 32px)' marginTop='24px'>
        <SrButton
          hasBoxShadow
          theme={SR_BUTTON_THEME_ADOBE_GREEN}
          className='buttonCta'
          onClick={_onClickMarketProperty}
        >
          {buttonLabel}
        </SrButton>
      </SrBox>
      {isReferralAgreementModalOpen && (
        <SrPartnerReferralAgreement2023Modal
          partner={partner}
          onClose={() => {
            setIsReferralAgreementModalOpen(false);
          }}
          onSubmit={() => {
            onAcceptReferralAgreement2023();
            setIsReferralAgreementModalOpen(false);
          }}
        />
      )}
    </SrBox>
  );
};

export default SrPartnerMarketListing;
